import { API_BASE, api } from './api'

/**
 * تنبيهات سطح المكتب (Web Push): الإشعار يصل تنبيهاً بنظام التشغيل (Windows/macOS)
 * ولو كان تبويب النظام مغلقاً — ما دام المتصفح يعمل.
 *
 * - المتصفح يشترك لدى خدمة دفعه بمفتاحنا العام ويرسل نقطة نهايته للخادم (`/api/push`).
 * - عامل الخدمة `public/sw.js` يستقبل الرسالة ويعرض التنبيه ويفتح شاشته عند النقر.
 * - الإذن يُطلب بنقرة الموظف (المتصفحات ترفض طلبه بلا تفاعل) ثم يُزامَن الاشتراك بصمت
 *   مع كل دخول، ويُفكّ عند الخروج فلا يصل من يدخل بعده شيءٌ من إشعارات سابقه.
 */

export type PushPermission = 'unsupported' | 'default' | 'denied' | 'granted'

export interface DesktopPushState {
  permission: PushPermission
  /** الاشتراك مُسجَّل بالخادم لهذا الحساب — التنبيهات تصل فعلاً */
  subscribed: boolean
  busy: boolean
  /**
   * الإذن ممنوح لكن تجديد الاشتراك بصمت تعذّر (Safari يشترط أن يكون بنقرة) —
   * تُعرض دعوة «إعادة التفعيل» بزرٍّ واحد
   */
  needsAction: boolean
}

const OFF_KEY = 'ufuq_push_off'
/** مفتاح الخادم العام الذي أُنشئ به اشتراك هذا المتصفح — لمتصفحٍ لا يكشفه */
const KEY_STORE = 'ufuq_push_vapid_key'
const SW_URL = `/sw.js?api=${encodeURIComponent(API_BASE)}`

let state: DesktopPushState = {
  permission: readPermission(),
  subscribed: false,
  busy: false,
  needsAction: false,
}
const listeners = new Set<() => void>()

function setState(patch: Partial<DesktopPushState>) {
  state = { ...state, ...patch }
  listeners.forEach((l) => l())
}

export function getDesktopPushState(): DesktopPushState {
  return state
}

export function subscribeDesktopPush(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window &&
    window.isSecureContext !== false
  )
}

function readPermission(): PushPermission {
  return pushSupported() ? Notification.permission : 'unsupported'
}

/** الموظف أطفأها بنفسه — لا نعيد تشغيلها بصمت مع الدخول التالي */
function turnedOffByUser(): boolean {
  try {
    return localStorage.getItem(OFF_KEY) === '1'
  } catch {
    return false
  }
}

function rememberOff(off: boolean) {
  try {
    if (off) localStorage.setItem(OFF_KEY, '1')
    else localStorage.removeItem(OFF_KEY)
  } catch {
    /* تخزين محظور — لا يهم */
  }
}

function readStoredKey(): string | null {
  try {
    return localStorage.getItem(KEY_STORE)
  } catch {
    return null
  }
}

function storeKey(publicKey: string) {
  try {
    localStorage.setItem(KEY_STORE, publicKey)
  } catch {
    /* تخزين محظور — المقارنة تعتمد مفتاح المتصفح نفسه */
  }
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}

/**
 * هل أُنشئ هذا الاشتراك بمفتاح الخادم الحالي؟ من المتصفح إن كشف مفتاحه، وإلا ممّا حُفظ يوم
 * أُنشئ — واشتراكٌ بلا أثرٍ يُجدَّد مرة. اشتراكٌ بمفتاحٍ آخر ترفض خدمة الدفع كل تنبيهٍ له
 * (Apple ‏400 وGoogle ‏403) فلا يصل الموظف شيء.
 */
function madeWithKey(sub: PushSubscription, key: Uint8Array, publicKey: string): boolean {
  const own = sub.options?.applicationServerKey
  if (own) return sameBytes(new Uint8Array(own), key)
  return readStoredKey() === publicKey
}

export const pushApi = {
  vapidPublicKey: () =>
    api
      .get<{ enabled: boolean; publicKey: string }>('/api/push/vapid-public-key')
      .then((r) => r.data),
  subscribe: (sub: PushSubscriptionJSON) =>
    api.post('/api/push/subscriptions', sub).then(() => undefined),
  unsubscribe: (endpoint: string) =>
    api.post('/api/push/unsubscribe', { endpoint }).then(() => undefined),
  /** تنبيه تجريبي لمتصفحات هذا الحساب — يعيد عددها */
  test: () => api.post<{ subscriptions: number }>('/api/push/test').then((r) => r.data),
}

let registration: Promise<ServiceWorkerRegistration | null> | null = null

/** مفتاح الخادم يُجلب مرة: النقرة تصل للاشتراك فوراً (Safari يُسقط أثر النقرة بعد انتظار الشبكة) */
let serverKey: Promise<{ enabled: boolean; publicKey: string }> | null = null
function loadServerKey() {
  if (!serverKey) {
    serverKey = pushApi.vapidPublicKey().catch((err) => {
      serverKey = null
      throw err
    })
  }
  return serverKey
}

/** تسجيل عامل الخدمة مرة واحدة (بلا تخزين HTTP للملف — كل نشرٍ يصل فوراً) */
export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return Promise.resolve(null)
  if (!registration) {
    registration = navigator.serviceWorker
      .register(SW_URL, { scope: '/', updateViaCache: 'none' })
      .catch((err) => {
        console.warn('[push] service worker registration failed:', err)
        registration = null
        return null
      })
  }
  return registration
}

/** applicationServerKey بصيغة البايتات كما يطلب المتصفح */
export function urlBase64ToUint8Array(base64Url: string): Uint8Array<ArrayBuffer> {
  const padded = base64Url + '='.repeat((4 - (base64Url.length % 4)) % 4)
  const raw = atob(padded.replace(/-/g, '+').replace(/_/g, '/'))
  const bytes = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return bytes
}

async function currentSubscription(): Promise<{
  reg: ServiceWorkerRegistration
  sub: PushSubscription | null
} | null> {
  const reg = await registerServiceWorker()
  if (!reg) return null
  return { reg, sub: await reg.pushManager.getSubscription() }
}

/**
 * مزامنة صامتة مع كل دخول: الإذن ممنوح والموظف لم يطفئها → نضمن اشتراكاً مربوطاً بحسابه.
 * (متصفحٌ مشترك بين موظفين: الاشتراك ينتقل لمن دخل الآن.)
 */
export async function syncDesktopPush(): Promise<boolean> {
  setState({ permission: readPermission() })
  if (state.permission !== 'granted' || turnedOffByUser()) {
    setState({ subscribed: false, needsAction: false })
    return false
  }
  setState({ busy: true })
  // نقطة نهاية اشتراكٍ بمفتاحٍ سابق فُكّ محلياً — يُحذف صفّها بالخادم مهما انتهى التجديد
  let stale: string | null = null
  try {
    const cur = await currentSubscription()
    if (!cur) return false
    const { enabled, publicKey } = await loadServerKey()
    if (!enabled || !publicKey) return false
    const applicationServerKey = urlBase64ToUint8Array(publicKey)

    let sub = cur.sub
    if (sub && !madeWithKey(sub, applicationServerKey, publicKey)) {
      stale = sub.endpoint
      await sub.unsubscribe().catch(() => false)
      sub = null
    }
    if (!sub) {
      try {
        sub = await cur.reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey })
      } catch {
        // اشتراكٌ قديم بمفتاحٍ آخر يمنع الجديد — نفكّه ونعيد
        await (await cur.reg.pushManager.getSubscription())?.unsubscribe()
        sub = await cur.reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey })
      }
      storeKey(publicKey)
    }
    if (stale && stale !== sub.endpoint) {
      await pushApi.unsubscribe(stale).catch(() => {})
      stale = null
    }
    await pushApi.subscribe(sub.toJSON())
    setState({ subscribed: true, needsAction: false })
    return true
  } catch (err) {
    console.warn('[push] subscription sync failed:', err)
    if (stale) void pushApi.unsubscribe(stale).catch(() => {})
    setState({ subscribed: false, needsAction: true })
    return false
  } finally {
    setState({ busy: false })
  }
}

/**
 * بنقرة الموظف: طلب الإذن (يجب أن يكون أول ما يحدث بالنقرة — Safari يشترط ذلك)
 * ثم الاشتراك. يعيد الإذن الناتج.
 */
export async function enableDesktopPush(): Promise<PushPermission> {
  if (!pushSupported()) return 'unsupported'
  rememberOff(false)
  const permission = await Notification.requestPermission()
  setState({ permission })
  if (permission === 'granted') await syncDesktopPush()
  return permission
}

/** الموظف أطفأها: يُفكّ الاشتراك بالخادم والمتصفح (إذن المتصفح يبقى؛ التشغيل يعود بنقرة) */
export async function disableDesktopPush(): Promise<void> {
  rememberOff(true)
  await releaseDesktopPush({ server: true })
}

/**
 * فكّ اشتراك هذا المتصفح — عند الخروج: `server` يحذف صفّه بالخادم (يحتاج توكناً)،
 * ودائماً يُفكّ محلياً فيُنشأ اشتراكٌ جديد لمن يدخل بعده.
 */
export async function releaseDesktopPush(opts: { server: boolean }): Promise<void> {
  setState({ subscribed: false })
  if (!pushSupported()) return
  try {
    const cur = await currentSubscription()
    const sub = cur?.sub
    if (!sub) return
    if (opts.server) await pushApi.unsubscribe(sub.endpoint).catch(() => {})
    await sub.unsubscribe().catch(() => {})
  } catch {
    /* best-effort */
  }
}

// خروجٌ تلقائي (انتهت الجلسة): لا توكن لحذف الصفّ بالخادم — يُفكّ محلياً ويُنظَّف
// الصفّ عند أول إرسالٍ يفشل (410)
if (typeof window !== 'undefined') {
  window.addEventListener('ufuq-logout', () => {
    void releaseDesktopPush({ server: false })
  })
}
